import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import TimelineIcon from "@mui/icons-material/Timeline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Stack,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";

import ProjectWorkflowFoundationApi from "../../../../../infrastructure/api/projectWorkflowFoundation";

const pick = (obj, ...keys) => {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
};

const WorkspaceOperationalBanner = ({ requestId, source }) => {
  const [loading, setLoading] = useState(true);
  const [workflowError, setWorkflowError] = useState("");
  const [finGate, setFinGate] = useState(null);
  const [workflowStage, setWorkflowStage] = useState("");

  const load = useCallback(async () => {
    if (!requestId) return;
    setLoading(true);
    setWorkflowError("");

    const [wfRes, finRes] = await Promise.all([
      ProjectWorkflowFoundationApi.getWorkflowV2StatusSummary(source, requestId),
      ProjectWorkflowFoundationApi.getProjectFinancialExecutionGateStatus(source, requestId),
    ]);

    if (!wfRes.ok) {
      setWorkflowError(
        wfRes.message ||
          "بارگذاری وضعیت WorkflowV2 ناموفق بود. Timeline ممکن است ناقص باشد."
      );
    } else {
      const stage =
        pick(wfRes.data, "currentStageTitle", "CurrentStageTitle") ||
        pick(wfRes.data, "currentStage", "CurrentStage") ||
        "";
      setWorkflowStage(String(stage));
    }

    if (!finRes.ok) {
      setFinGate({ error: finRes.message || "بررسی گیت مالی اجرا ناموفق بود." });
    } else {
      setFinGate(finRes.data);
    }

    setLoading(false);
  }, [requestId, source]);

  useEffect(() => {
    load();
  }, [load]);

  const alerts = useMemo(() => {
    const items = [];

    if (workflowError) {
      items.push({
        severity: "warning",
        icon: <TimelineIcon fontSize="inherit" />,
        title: "WorkflowV2",
        message: workflowError,
      });
    }

    if (finGate?.error) {
      items.push({
        severity: "error",
        icon: <AccountBalanceWalletIcon fontSize="inherit" />,
        title: "گیت مالی",
        message: finGate.error,
      });
    } else if (finGate) {
      const satisfied = pick(finGate, "isSatisfied", "IsSatisfied");
      const block = pick(finGate, "blockingReason", "BlockingReason");
      const unpaid = pick(finGate, "unpaidInvoiceCount", "UnpaidInvoiceCount") ?? 0;
      const partial = pick(finGate, "partialInvoiceCount", "PartialInvoiceCount") ?? 0;
      if (satisfied === false) {
        items.push({
          severity: "warning",
          icon: <AccountBalanceWalletIcon fontSize="inherit" />,
          title: "گیت مالی اجرا",
          message:
            block ||
            (unpaid > 0
              ? `${unpaid} فاکتور پرداخت‌نشده — فعال‌سازی اجرا مجاز نیست.`
              : partial > 0
                ? `${partial} فاکتور با پرداخت جزئی — تکمیل پرداخت لازم است.`
                : "فاکتور الزامی تسویه نشده است."),
        });
      }
      const profileSync = pick(finGate, "profileOutOfSync", "ProfileOutOfSync");
      const recon = pick(finGate, "reconciliationWarning", "ReconciliationWarning");
      if (profileSync && recon) {
        items.push({
          severity: "info",
          icon: <WarningAmberIcon fontSize="inherit" />,
          title: "هم‌خوانی مالی",
          message: recon,
        });
      }
    }

    return items;
  }, [workflowError, finGate]);

  if (loading) {
    return (
      <Box sx={{ py: 1, display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">
          بررسی وضعیت عملیاتی پرونده…
        </Typography>
      </Box>
    );
  }

  if (alerts.length === 0 && !workflowStage) {
    return null;
  }

  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      {workflowStage ? (
        <Chip
          size="small"
          icon={<TimelineIcon />}
          label={`مرحله جاری: ${workflowStage}`}
          variant="outlined"
          color="primary"
        />
      ) : null}
      <Collapse in={alerts.length > 0}>
        <Stack spacing={1}>
          {alerts.map((a, idx) => (
            <Alert key={idx} severity={a.severity} icon={a.icon}>
              <Typography variant="subtitle2">{a.title}</Typography>
              <Typography variant="body2">{a.message}</Typography>
            </Alert>
          ))}
        </Stack>
      </Collapse>
    </Stack>
  );
};

WorkspaceOperationalBanner.propTypes = {
  requestId: PropTypes.number.isRequired,
  source: PropTypes.string.isRequired,
};

export default WorkspaceOperationalBanner;
